/**
 * 删除项目二次确认弹窗。
 *
 * ⚠️ 后端为**软删除**（deletedAt），数据可恢复；
 *    但项目下若仍有未删除任务，后端会拒绝并返回 `PROJECT_HAS_TASKS`，
 *    该 message 会原样展示在弹窗里（不吞错）。
 */

import Button from '../../components/Button';
import Modal from '../../components/Modal';
import { ApiError } from '../../api/client';
import { useDeleteProject } from './useProjects';

/** 二次确认只需要这三个字段（地图光点 / 列表行均可满足） */
export interface DeletableProject {
  id: string;
  code: string;
  name: string;
}

export interface DeleteProjectDialogProps {
  project: DeletableProject | null;
  open: boolean;
  onClose(): void;
  onDeleted?(id: string): void;
}

export default function DeleteProjectDialog({
  project,
  open,
  onClose,
  onDeleted,
}: DeleteProjectDialogProps) {
  const remove = useDeleteProject();

  const confirm = () => {
    if (!project) return;
    remove.mutate(project.id, {
      onSuccess: () => {
        onDeleted?.(project.id);
        onClose();
      },
    });
  };

  const errorMessage =
    remove.error instanceof ApiError
      ? remove.error.message
      : remove.error instanceof Error
        ? remove.error.message
        : null;

  return (
    <Modal
      open={open}
      size="sm"
      danger
      title={`删除项目 · ${project?.name ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={remove.isPending}>
            取消
          </Button>
          <Button variant="danger" onClick={confirm} disabled={remove.isPending}>
            {remove.isPending ? '删除中…' : '确认删除'}
          </Button>
        </>
      }
    >
      <div className="pm-delete-meta">
        项目编号：<b>{project?.code ?? '—'}</b>
      </div>

      <div className="pm-delete-explain">
        删除后该项目及其关联任务将不再显示（<b>软删除，数据可恢复</b>）。
        <br />
        若项目下仍有未删除的任务，系统会拒绝删除，请先处理相关任务。
      </div>

      {errorMessage && <div className="pm-modal__err">⚠ {errorMessage}</div>}
    </Modal>
  );
}
